/**
 * Phase 34 (D107) — single source of truth for the subscription tier
 * feature catalog.
 *
 * Both the merchant-facing Plans page and the admin Plan-changes
 * approval queue render from this object so they can never drift.
 * Adding a new feature to PRO is a single edit here.
 *
 * Quotas are descriptive — this catalog does NOT enforce per-tenant
 * limits. Existing per-tier enforcement (wherever in the codebase
 * reads `tenants.subscription_tier`) keeps doing what it does today.
 * The values below are the human-facing copy for the cards.
 */
import { z } from "zod";

export const SUBSCRIPTION_TIERS = ["FREE", "PRO", "ELITE"] as const;
export type SubscriptionTier = (typeof SUBSCRIPTION_TIERS)[number];

export const BillingCycleSchema = z.enum(["MONTHLY", "YEARLY"]);
export type BillingCycle = z.infer<typeof BillingCycleSchema>;

export const PlanChangeStatusSchema = z.enum([
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
]);
export type PlanChangeStatus = z.infer<typeof PlanChangeStatusSchema>;

export interface TierFeatureRow {
  /** Short label shown next to the value. */
  label: string;
  /** Human-readable value for each tier. Use "Unlimited" for no cap. */
  values: Record<SubscriptionTier, string>;
}

export interface TierCatalogEntry {
  tier: SubscriptionTier;
  /** Display name for the card header. */
  name: string;
  /** One-sentence pitch. */
  tagline: string;
  /** Headline price string (PHP). Free is "₱0". */
  monthlyPricePhp: string;
  yearlyPricePhp: string;
  /** Marketing-style bullet list rendered above the feature matrix. */
  highlights: string[];
}

/**
 * Hard quotas backing the Plans copy. Used by the post-approval
 * over-quota banner so the merchant sees an inline warning when
 * their live usage exceeds the new tier's caps.
 *
 * `null` means "no cap" (Elite). Add new keys here as enforcement
 * expands; the over-quota check ignores keys it doesn't have a
 * usage source for.
 */
export interface TierQuotas {
  enrolledCustomers: number | null;
  monthlyTransactions: number | null;
  monthlyPushNotifications: number | null;
}

export const TIER_QUOTAS: Readonly<Record<SubscriptionTier, TierQuotas>> =
  Object.freeze({
    FREE: {
      enrolledCustomers: 500,
      monthlyTransactions: 2_000,
      monthlyPushNotifications: 100,
    },
    PRO: {
      enrolledCustomers: 5_000,
      monthlyTransactions: 20_000,
      monthlyPushNotifications: 5_000,
    },
    ELITE: {
      enrolledCustomers: null,
      monthlyTransactions: null,
      monthlyPushNotifications: null,
    },
  });

export const TIER_CATALOG: Readonly<Record<SubscriptionTier, TierCatalogEntry>> =
  Object.freeze({
    FREE: {
      tier: "FREE",
      name: "Free",
      tagline: "Get loyalty running today, no credit card.",
      monthlyPricePhp: "₱0",
      yearlyPricePhp: "₱0",
      highlights: [
        "Up to 500 enrolled customers",
        "Stamps, points, or hybrid loyalty",
        "QR-based join + cashier scan",
        "Basic merchant dashboard",
      ],
    },
    PRO: {
      tier: "PRO",
      name: "Pro",
      tagline: "Grow with marketing tools, surprises, and richer analytics.",
      monthlyPricePhp: "₱1,499",
      yearlyPricePhp: "₱14,990",
      highlights: [
        "Up to 5,000 enrolled customers",
        "Marketplace claim codes (vouchers)",
        "Surprise pool + streak rewards",
        "Push + inbox campaigns",
        "Year-in-Review for customers",
      ],
    },
    ELITE: {
      tier: "ELITE",
      name: "Elite",
      tagline: "Multi-branch chains, premium support, and full ecosystem access.",
      monthlyPricePhp: "₱3,999",
      yearlyPricePhp: "₱39,990",
      highlights: [
        "Unlimited enrolled customers",
        "Vertical OS integration (CarWash OS / Baresca / HAOS)",
        "Custom export + analytics access",
        "Priority M42 ops support",
        "Sandbox API token",
      ],
    },
  });

/**
 * Feature matrix rendered as a small table on the Plans page. Each
 * row shows what the tier offers for that capability. Values are
 * descriptive copy, not enforcement primitives.
 */
export const TIER_FEATURE_MATRIX: ReadonlyArray<TierFeatureRow> = Object.freeze([
  {
    label: "Enrolled customers",
    values: { FREE: "Up to 500", PRO: "Up to 5,000", ELITE: "Unlimited" },
  },
  {
    label: "Transactions / month",
    values: { FREE: "Up to 2,000", PRO: "Up to 20,000", ELITE: "Unlimited" },
  },
  {
    label: "Marketplace claim codes",
    values: { FREE: "—", PRO: "Up to 1,000 / month", ELITE: "Unlimited" },
  },
  {
    label: "Surprise pool slots",
    values: { FREE: "—", PRO: "Up to 50 active", ELITE: "Unlimited" },
  },
  {
    label: "Push notifications / month",
    values: { FREE: "100", PRO: "5,000", ELITE: "Unlimited" },
  },
  {
    label: "Year-in-Review batch",
    values: { FREE: "—", PRO: "Included", ELITE: "Included" },
  },
  {
    label: "Vertical OS integration",
    values: { FREE: "—", PRO: "—", ELITE: "Included" },
  },
  {
    label: "Sandbox API token",
    values: { FREE: "—", PRO: "—", ELITE: "Included" },
  },
  {
    label: "Support",
    values: {
      FREE: "Community",
      PRO: "Email (next business day)",
      ELITE: "Priority M42 ops",
    },
  },
]);
